import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { channelWa, media } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Skeleton, Loader } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, BreadcrumbTrail } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Textarea, Select } from '@repletoryreach/sdk-ui/inputs';
import { Dropzone } from '@repletoryreach/sdk-ui/dropzone';
import { ImageCropper, type CropState, type ImageCropperResult } from '@repletoryreach/sdk-ui/image-cropper';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';
import { imageCropperLabels } from '../../../components/imageCropperLabels';
import { formatTime } from '../MetaRefusalNotice';
import { ProfilePreview, type ProfilePreviewValues } from './ProfilePreview';
import { ProfileRulesList } from './ProfileRulesList';
import { checkProfile, firstPerField, withFallback, type CheckField, type ProfileCheck } from './rules';
import { PICTURE_FIELD, readSaveError, type SaveErrorView } from './saveError';
import './profile.css';

/**
 * WhatsApp shows the profile picture as a circle and takes a square JPG. Meta
 * refuses anything under 192 on a side outright (code 1, subcode 3441013, as
 * found on 15 October 2026) and recommends 640 × 640. So no picture goes up as
 * it was chosen: every one passes through the picture editor, which makes
 * exactly the recommended square, flattened onto white (WhatsApp shows a
 * transparent background as black). The sizes come with the profile read.
 */
const PICTURE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const PICTURE_FILE_NAME = 'profile-picture.jpg';
const PICTURE_BACKGROUND = '#ffffff';
const PICTURE_QUALITY = 0.9;

type Fields = {
  about: string;
  address: string;
  description: string;
  email: string;
  vertical: string;
  websites: string[];
};

const BLANK: Fields = { about: '', address: '', description: '', email: '', vertical: '', websites: ['', ''] };

/** A picture the editor made, waiting for Save, with the original kept so the crop can be changed. */
type PendingPicture = { file: File; url: string; original: File; crop: CropState };

/** The words for one problem the page found before sending. */
function describeCheck(c: ProfileCheck, t: Translate): string {
  switch (c.code) {
    case 'tooLong': return t('numbers.profile.check.tooLong', c.vars);
    case 'aboutEmpty': return t('numbers.profile.check.aboutEmpty');
    case 'emailInvalid': return t('numbers.profile.check.emailInvalid');
    case 'websiteScheme': return t('numbers.profile.check.websiteScheme');
    case 'websitesTooMany': return t('numbers.profile.check.websitesTooMany', c.vars);
  }
}

function fieldLabel(field: CheckField, t: Translate): string {
  switch (field) {
    case 'about': return t('numbers.profile.fields.about');
    case 'description': return t('numbers.profile.fields.description');
    case 'address': return t('numbers.profile.fields.address');
    case 'email': return t('numbers.profile.fields.email');
    case 'website1': return t('numbers.profile.fields.website1');
    case 'website2': return t('numbers.profile.fields.website2');
  }
}

/**
 * The business profile behind one number: the picture, the status line, the
 * description, the address, the email, the websites and the category a
 * recipient sees when they tap the number a message came from.
 *
 * <p>Everything on this page is public. That is not obvious, so it is said
 * once at the top and not repeated field by field.</p>
 *
 * <p>The picture is the customer's own logo. There is deliberately no default
 * and no suggestion of ours: a florist's customers must see the florist.</p>
 */
export default function BusinessProfilePage() {
  const t = useT();
  const qc = useQueryClient();
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const { data, isPending, error } = useQuery({
    queryKey: ['business-profile', id],
    queryFn: () => channelWa.businessProfile(id),
    enabled: !!id
  });

  const limits = useMemo(() => withFallback(data?.meta?.limits), [data?.meta?.limits]);
  const verticals = data?.meta?.verticals ?? [];
  const number = data?.meta?.number;
  const pictureMb = Math.round(limits.picture_bytes / (1024 * 1024));

  const [fields, setFields] = useState<Fields>(BLANK);
  const [picture, setPicture] = useState<PendingPicture | null>(null);
  const [cropping, setCropping] = useState<{ file: File; crop: CropState | null } | null>(null);
  const [pictureError, setPictureError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<SaveErrorView | null>(null);

  const cropperLabels = useMemo(() => ({
    ...imageCropperLabels(t),
    title: t('numbers.profile.picture.cropperTitle'),
    fillNote: t('numbers.profile.picture.fillWhite')
  }), [t]);

  /** Seed the form once the profile lands, and again after a save. */
  useEffect(() => {
    const p = data?.data;
    if (!p) return;
    setFields({
      about: p.about ?? '',
      address: p.address ?? '',
      description: p.description ?? '',
      email: p.email ?? '',
      vertical: p.vertical ?? '',
      websites: [p.websites?.[0] ?? '', p.websites?.[1] ?? '']
    });
  }, [data?.data]);

  // A picked file lives as an object URL until it is saved or replaced.
  useEffect(() => () => { if (picture) URL.revokeObjectURL(picture.url); }, [picture]);

  function set<K extends keyof Fields>(key: K, value: Fields[K]) {
    setFields((f) => ({ ...f, [key]: value }));
    setFieldErrors((e) => (e[key] ? { ...e, [key]: '' } : e));
  }

  /*
   * What to send: the fields that differ from what Meta holds, and nothing
   * else. A save must not be able to clear something the person never changed,
   * and it must not re-send a field they typed in and put back the way it was:
   * WhatsApp refuses an empty status line, so a field that merely lost focus
   * would otherwise come back as a refusal about a value nobody meant to set.
   */
  const loaded = data?.data;
  const changes = useMemo((): channelWa.UpdateBusinessProfileRequest => {
    const req: channelWa.UpdateBusinessProfileRequest = {};
    if (fields.about !== (loaded?.about ?? '')) req.about = fields.about;
    if (fields.address !== (loaded?.address ?? '')) req.address = fields.address;
    if (fields.description !== (loaded?.description ?? '')) req.description = fields.description;
    if (fields.email !== (loaded?.email ?? '')) req.email = fields.email;
    if (fields.vertical !== (loaded?.vertical ?? '')) req.vertical = fields.vertical;
    const next = fields.websites.map((w) => w.trim()).filter(Boolean);
    const now = loaded?.websites ?? [];
    if (next.length !== now.length || next.some((w, i) => w !== now[i])) req.websites = next;
    return req;
  }, [fields, loaded]);

  const dirty = Object.keys(changes).length > 0 || picture !== null;

  /*
   * Checked here before anything is sent, under each field as it is typed, so
   * most of what WhatsApp would refuse never leaves the page. Save stays off
   * while anything is wrong, with a line beside it saying what.
   */
  const checks = useMemo(() => checkProfile(fields, changes, limits), [fields, changes, limits]);
  const checkByField = useMemo(() => firstPerField(checks), [checks]);
  const checkError = (f: CheckField) => {
    const c = checkByField[f];
    return c ? describeCheck(c, t) : undefined;
  };
  const blockedFields = useMemo(
    () => [...new Set(checks.map((c) => c.field))].map((f) => fieldLabel(f, t)),
    [checks, t]
  );

  const save = useMutation({
    mutationFn: async () => {
      const req: channelWa.UpdateBusinessProfileRequest = { ...changes };

      /*
       * The picture goes to the media library first and reaches Meta as an id.
       * channel-wa fetches the bytes internally and pushes them to Meta's
       * resumable upload API: a signed URL from here is signed for a browser,
       * not for one service calling another.
       */
      if (picture) {
        const up = await media.upload(picture.file, { filename: picture.file.name });
        req.profile_picture_media_id = up.data.id;
      }
      return channelWa.updateBusinessProfile(id, req);
    },
    onMutate: () => setSaveError(null),
    onSuccess: () => {
      setPicture(null);
      setFieldErrors({});
      setSaveError(null);
      qc.invalidateQueries({ queryKey: ['business-profile', id] });
      qc.invalidateQueries({ queryKey: ['phone-numbers'] });
      notifySuccess(t('numbers.profile.toast.saved'), t('numbers.profile.title'));
    },
    onError: (e: unknown) => {
      /*
       * The words are this console's own, by the platform's code, with the
       * limits the page counts against. Anything about a field goes on the
       * field; what to do next sits beside the Save button and in the toast.
       */
      const view = readSaveError(e, t, limits, formatTime);
      setFieldErrors(view.fieldErrors);
      setSaveError(view);
      const toast = view.next && !view.checklist
        ? `${view.message} ${view.next}`
        : `${view.message} ${t('numbers.profile.error.seeBelow')}`;
      notifyError(toast, t('toast.saveFailed'));
    }
  });

  const refresh = useMutation({
    mutationFn: () => channelWa.businessProfile(id, { refresh: true }),
    onSuccess: (fresh) => {
      qc.setQueryData(['business-profile', id], fresh);
      notifySuccess(t('numbers.profile.toast.refreshed'), t('numbers.profile.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('numbers.profile.toast.refreshFailed'))
  });

  /** Every picture goes through the editor: chosen here, made square and sized there. */
  function pickPicture(files: File[]) {
    const file = files[0];
    if (!file) return;
    setPictureError(null);
    if (!PICTURE_TYPES.includes(file.type)) {
      setPictureError(t('numbers.profile.picture.wrongType'));
      return;
    }
    if (file.size > limits.picture_bytes) {
      setPictureError(t('numbers.profile.picture.tooBig', { max: pictureMb }));
      return;
    }
    setCropping({ file, crop: null });
  }

  function rejectPicture(rejections: Array<{ file: File; reasons: string[] }>) {
    const tooBig = rejections.some((r) => r.reasons.includes('file-too-large'));
    setPictureError(tooBig ? t('numbers.profile.picture.tooBig', { max: pictureMb }) : t('numbers.profile.picture.wrongType'));
  }

  function takeCropped(result: ImageCropperResult) {
    const original = cropping?.file;
    if (!original) return;
    if (picture) URL.revokeObjectURL(picture.url);
    setPicture({ file: result.file, url: URL.createObjectURL(result.file), original, crop: result.crop });
    setCropping(null);
    setPictureError(null);
    setFieldErrors((e) => (e[PICTURE_FIELD] ? { ...e, [PICTURE_FIELD]: '' } : e));
  }

  function dropPicture() {
    if (picture) URL.revokeObjectURL(picture.url);
    setPicture(null);
  }

  const shownPicture = picture?.url ?? data?.data?.profile_picture_url ?? null;

  const preview: ProfilePreviewValues = {
    about: fields.about,
    address: fields.address,
    description: fields.description,
    email: fields.email,
    vertical: fields.vertical,
    websites: fields.websites,
    pictureUrl: shownPicture
  };

  const verticalLabel = useMemo(
    () => (code: string) => t(`numbers.profile.vertical.${code}`, code),
    [t]
  );

  const numberName = number?.display_name || number?.msisdn || t('numbers.profile.thisNumber');

  return (
    <Stack gap="md">
      <BreadcrumbTrail
        items={[{ label: t('nav.numbers'), href: '/numbers' }, { label: t('numbers.profile.title') }]}
        renderLink={(href, label) => <Link to={href}>{label}</Link>}
      />

      <PageHeader
        title={t('numbers.profile.title')}
        description={t('numbers.profile.description', { number: numberName })}
        withDivider={false}
        actions={(
          <Group gap="xs" wrap="wrap">
            <Button
              size="sm"
              variant="subtle"
              loading={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              {t('numbers.profile.refresh')}
            </Button>
            <Button size="sm" variant="default" onClick={() => navigate('/numbers')}>
              {t('numbers.profile.back')}
            </Button>
          </Group>
        )}
      />

      {/*
        Said once, at the top, plainly. Nothing else on this page repeats it,
        because a warning on every field reads as a warning about nothing.
      */}
      <Alert color="info" title={t('numbers.profile.public.title')}>
        {t('numbers.profile.public.body')}
      </Alert>

      {error && <Alert color="error" role="alert">{t('numbers.profile.loadError')}</Alert>}

      {isPending ? (
        <Card withBorder><Stack gap="sm"><Skeleton height={20} /><Skeleton height={120} /><Skeleton height={80} /></Stack></Card>
      ) : (
        <div className="rr-waprofile">
          <Stack gap="md">
            {/* ── The picture ───────────────────────────────────────────── */}
            <Card withBorder>
              <Stack gap="sm">
                <Text fw="semibold">{t('numbers.profile.picture.heading')}</Text>
                {/*
                  Not our logo, and not a stock one either. Whoever receives the
                  message is looking at this business, not at the platform it
                  happens to run on.
                */}
                <Text role="bodySmall" c="var(--rr-color-text-muted)">
                  {t('numbers.profile.picture.yours')}
                </Text>

                <div className="rr-waprofile__picture">
                  <div className="rr-waprofile__crop">
                    {shownPicture ? (
                      <>
                        <img src={shownPicture} alt={t('numbers.profile.picture.currentAlt')} />
                        {/* Everything outside the circle is what WhatsApp cuts. */}
                        <div className="rr-waprofile__cropmask" />
                        <div className="rr-waprofile__cropring" />
                      </>
                    ) : (
                      <div className="rr-waprofile__nopicture">{t('numbers.profile.picture.none')}</div>
                    )}
                  </div>

                  <div className="rr-waprofile__pictureside">
                    <Dropzone
                      onDrop={pickPicture}
                      onReject={rejectPicture}
                      accept={PICTURE_TYPES}
                      maxSize={limits.picture_bytes}
                      maxFiles={1}
                      multiple={false}
                      loading={save.isPending}
                    >
                      <Stack gap={4} align="center">
                        <Text role="bodySmall" fw="medium">{t('numbers.profile.picture.drop')}</Text>
                        <Text role="caption" c="var(--rr-color-text-muted)">
                          {t('numbers.profile.picture.rules', { max: pictureMb, size: limits.picture_edge })}
                        </Text>
                      </Stack>
                    </Dropzone>

                    <Text role="caption" c="var(--rr-color-text-muted)">
                      {t('numbers.profile.picture.crop')}
                    </Text>

                    {picture && (
                      <Group gap="xs" wrap="wrap">
                        <Text role="caption">
                          {t('numbers.profile.picture.pending', { name: picture.file.name, size: limits.picture_edge })}
                        </Text>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => setCropping({ file: picture.original, crop: picture.crop })}
                        >
                          {t('numbers.profile.picture.editCrop')}
                        </Button>
                        <Button size="xs" variant="subtle" onClick={dropPicture}>
                          {t('action.cancel')}
                        </Button>
                      </Group>
                    )}
                    {pictureError && <Alert color="error" role="alert">{pictureError}</Alert>}
                    {fieldErrors[PICTURE_FIELD] && <Alert color="error" role="alert">{fieldErrors[PICTURE_FIELD]}</Alert>}
                  </div>
                </div>
              </Stack>
            </Card>

            {/* ── What WhatsApp accepts, before anybody saves ───────────── */}
            <Card withBorder>
              <Stack gap="xs">
                <Text fw="semibold">{t('numbers.profile.rules.title')}</Text>
                <ProfileRulesList limits={limits} />
              </Stack>
            </Card>

            {/* ── The words ─────────────────────────────────────────────── */}
            <Card withBorder>
              <Stack gap="md">
                <Text fw="semibold">{t('numbers.profile.details.heading')}</Text>

                <TextInput
                  label={t('numbers.profile.fields.about')}
                  value={fields.about}
                  maxLength={limits.about}
                  onChange={(e) => set('about', e.currentTarget.value)}
                  error={fieldErrors.about || checkError('about')}
                  description={t('numbers.profile.fields.about.hint', {
                    used: fields.about.length, max: limits.about
                  })}
                />

                <Textarea
                  label={t('numbers.profile.fields.description')}
                  value={fields.description}
                  maxLength={limits.description}
                  minRows={4}
                  onChange={(e) => set('description', e.currentTarget.value)}
                  error={fieldErrors.description || checkError('description')}
                  description={t('numbers.profile.fields.description.hint', {
                    used: fields.description.length, max: limits.description
                  })}
                />

                <Select
                  label={t('numbers.profile.fields.vertical')}
                  value={fields.vertical}
                  data={[
                    { value: '', label: t('numbers.profile.vertical.unset') },
                    ...verticals.map((v) => ({ value: v, label: verticalLabel(v) }))
                  ]}
                  onChange={(e) => set('vertical', e.currentTarget.value)}
                  error={fieldErrors.vertical || undefined}
                  description={t('numbers.profile.fields.vertical.hint')}
                />

                <Textarea
                  label={t('numbers.profile.fields.address')}
                  value={fields.address}
                  maxLength={limits.address}
                  minRows={2}
                  onChange={(e) => set('address', e.currentTarget.value)}
                  error={fieldErrors.address || checkError('address')}
                  description={t('numbers.profile.fields.address.hint', {
                    used: fields.address.length, max: limits.address
                  })}
                />

                <TextInput
                  type="email"
                  label={t('numbers.profile.fields.email')}
                  value={fields.email}
                  maxLength={limits.email}
                  onChange={(e) => set('email', e.currentTarget.value)}
                  error={fieldErrors.email || checkError('email')}
                  description={t('numbers.profile.fields.email.hint', {
                    used: fields.email.length, max: limits.email
                  })}
                />

                {/*
                  Two boxes, because WhatsApp shows two. A third would be
                  refused by Meta with a parameter error naming neither the
                  field nor the count, so the limit is in the shape of the form.
                */}
                <TextInput
                  type="url"
                  label={t('numbers.profile.fields.website1')}
                  value={fields.websites[0]}
                  maxLength={limits.website}
                  placeholder="https://"
                  onChange={(e) => set('websites', [e.currentTarget.value, fields.websites[1] ?? ''])}
                  error={fieldErrors.websites || checkError('website1')}
                  description={t('numbers.profile.fields.website.hint')}
                />
                <TextInput
                  type="url"
                  label={t('numbers.profile.fields.website2')}
                  value={fields.websites[1]}
                  maxLength={limits.website}
                  placeholder="https://"
                  onChange={(e) => set('websites', [fields.websites[0] ?? '', e.currentTarget.value])}
                  error={checkError('website2')}
                  description={t('numbers.profile.fields.website2.hint', { max: limits.websites })}
                />

                {blockedFields.length > 0 && (
                  <Alert color="warning">
                    {t('numbers.profile.check.summary', { fields: blockedFields.join(', ') })}
                  </Alert>
                )}

                {saveError && (
                  <Alert
                    color={saveError.platform ? 'warning' : 'error'}
                    role="alert"
                    title={saveError.message}
                    withCloseButton
                    onClose={() => setSaveError(null)}
                  >
                    <Stack gap={6}>
                      {saveError.next && <Text role="bodySmall">{saveError.next}</Text>}
                      {saveError.checklist && <ProfileRulesList limits={limits} />}
                      {saveError.checklist && (
                        <Text role="bodySmall">
                          {saveError.reference
                            ? t('numbers.profile.error.stillFails', { reference: saveError.reference })
                            : t('numbers.profile.error.stillFailsNoRef')}
                        </Text>
                      )}
                    </Stack>
                  </Alert>
                )}

                <Group gap="xs" wrap="wrap">
                  <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty || checks.length > 0}>
                    {t('numbers.profile.save')}
                  </Button>
                  <Button
                    variant="subtle"
                    disabled={!dirty || save.isPending}
                    onClick={() => {
                      const p = data?.data;
                      setFields({
                        about: p?.about ?? '',
                        address: p?.address ?? '',
                        description: p?.description ?? '',
                        email: p?.email ?? '',
                        vertical: p?.vertical ?? '',
                        websites: [p?.websites?.[0] ?? '', p?.websites?.[1] ?? '']
                      });
                      dropPicture();
                      setPictureError(null);
                      setFieldErrors({});
                      setSaveError(null);
                    }}
                  >
                    {t('numbers.profile.discard')}
                  </Button>
                  {save.isPending && <Loader size="sm" />}
                </Group>

                <Text role="caption" c="var(--rr-color-text-muted)">
                  {t('numbers.profile.liveNote')}
                </Text>
              </Stack>
            </Card>
          </Stack>

          <div className="rr-waprofile__aside">
            <ProfilePreview
              values={preview}
              name={numberName}
              msisdn={number?.msisdn}
              verticalLabel={verticalLabel}
            />

            {/*
              Meta rate-limits profile reads, so this is held for a few minutes
              rather than asked on every visit. Saying when it was read is the
              difference between a cache and a lie.
            */}
            {data?.meta && (
              <Text role="caption" c="var(--rr-color-text-muted)">
                {data.meta.cached
                  ? t('numbers.profile.readAt.cached', { when: formatDateTime(data.meta.read_at) })
                  : t('numbers.profile.readAt.live', { when: formatDateTime(data.meta.read_at) })}
              </Text>
            )}
          </div>
        </div>
      )}

      <ImageCropper
        opened={!!cropping}
        file={cropping?.file ?? null}
        initialCrop={cropping?.crop ?? null}
        outputWidth={limits.picture_edge}
        aspect={1}
        minSourceEdge={limits.picture_min_edge}
        mask="circle"
        background={PICTURE_BACKGROUND}
        outputType="image/jpeg"
        quality={PICTURE_QUALITY}
        fileName={PICTURE_FILE_NAME}
        labels={cropperLabels}
        onCancel={() => setCropping(null)}
        onConfirm={takeCropped}
      />
    </Stack>
  );
}
